/**
 * Ambient layers: neon-city parallax, rain + dust canvas (~30fps), scan sweep,
 * gentle panel tilt; plus the THALIA typing feed, waveform and animated numbers.
 * All motion is dropped under prefers-reduced-motion.
 */
import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from './urgency';

export function HoloBackground() {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const RM = prefersReducedMotion();
    const fx = cvRef.current, city = cityRef.current;
    if (!fx || RM) return;
    const c2 = fx.getContext('2d');
    if (!c2) return;
    let W = 0, H = 0;
    const fs = () => { W = fx.width = window.innerWidth; H = fx.height = window.innerHeight; };
    fs();
    window.addEventListener('resize', fs);
    const rain = Array.from({ length: 110 }, () => ({ x: Math.random(), y: Math.random(), v: 0.004 + Math.random() * 0.004, l: 10 + Math.random() * 18 }));
    const dust = Array.from({ length: 40 }, () => ({ x: Math.random(), y: Math.random(), v: 0.00015 + Math.random() * 0.0003, r: 0.6 + Math.random() * 1.4 }));
    let mx = 0, my = 0;
    const mm = (e: MouseEvent) => { mx = e.clientX / window.innerWidth - 0.5; my = e.clientY / window.innerHeight - 0.5; };
    window.addEventListener('mousemove', mm);
    const fine = window.matchMedia?.('(pointer: fine)').matches;
    let raf = 0, last = 0, frame = 0;
    const loop = (ts: number) => {
      raf = requestAnimationFrame(loop);
      if (ts - last < 33 || document.hidden) return;
      last = ts;
      frame++;
      c2.clearRect(0, 0, W, H);
      c2.strokeStyle = 'rgba(180,170,255,.14)';
      c2.lineWidth = 1;
      c2.beginPath();
      rain.forEach(d => { d.y += d.v; if (d.y > 1) { d.y = -0.05; d.x = Math.random(); } const x = d.x * W + mx * -12, y = d.y * H; c2.moveTo(x, y); c2.lineTo(x - 2, y + d.l); });
      c2.stroke();
      c2.fillStyle = 'rgba(167,123,255,.4)';
      dust.forEach(d => { d.y -= d.v; if (d.y < 0) d.y = 1; c2.beginPath(); c2.arc(d.x * W + mx * -24, d.y * H, d.r, 0, 6.3); c2.fill(); });
      if (city) city.style.transform = `translate(${-mx * 30}px,${-my * 16}px)`;
      if (fine && frame % 2 === 0) {
        document.querySelectorAll<HTMLElement>('.holo-panel.tilt').forEach(p => {
          const b = p.getBoundingClientRect();
          if (b.bottom < 0 || b.top > H) return;
          const dx = (b.left + b.width / 2) / W - 0.5 - mx, dy = (b.top + b.height / 2) / H - 0.5 - my;
          p.style.transform = `perspective(1400px) rotateY(${(-dx * 2.2).toFixed(2)}deg) rotateX(${(dy * 2.2).toFixed(2)}deg)`;
        });
      }
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', fs); window.removeEventListener('mousemove', mm); };
  }, []);
  return (
    <>
      <div className="holo-city" ref={cityRef} aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>
      <canvas className="holo-fx" ref={cvRef} aria-hidden="true" />
      <div className="holo-sweep" aria-hidden="true" />
    </>
  );
}

/** Eases a number toward its target (animated numbers). */
export function AnimatedNumber({ value, decimals = 2 }: { value: number; decimals?: number }) {
  const [shown, setShown] = useState(value);
  const cur = useRef(value);
  useEffect(() => {
    if (prefersReducedMotion()) { cur.current = value; setShown(value); return; }
    let raf = 0;
    const eps = Math.pow(10, -decimals) / 2;
    const step = () => {
      cur.current += (value - cur.current) * 0.08;
      if (Math.abs(value - cur.current) < eps) { cur.current = value; setShown(value); return; }
      setShown(cur.current);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, decimals]);
  return <>{shown.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</>;
}

export interface FeedLine { id: string; ts: number; who?: string; text: string; hi?: string }

/** THALIA feed with a typing effect on the newest line. */
export function ThaliaFeed({ lines }: { lines: FeedLine[] }) {
  const RM = prefersReducedMotion();
  const newest = lines[0];
  const [typed, setTyped] = useState<{ id: string; n: number }>({ id: '', n: 0 });
  useEffect(() => {
    if (!newest || RM) return;
    const full = (newest.text + (newest.hi ? ' ' + newest.hi : '')).length;
    let n = 0;
    setTyped({ id: newest.id, n: 0 });
    const iv = setInterval(() => { n += 2; setTyped({ id: newest.id, n }); if (n >= full) clearInterval(iv); }, 28);
    return () => clearInterval(iv);
  }, [newest?.id, RM]); // eslint-disable-line react-hooks/exhaustive-deps
  const time = (ts: number) => new Date(ts).toTimeString().slice(0, 8);
  return (
    <div className="holo-log" aria-live="polite">
      {lines.slice(0, 7).map((l, i) => {
        const full = `${l.text}${l.hi ? ' ' + l.hi : ''}`;
        const typing = i === 0 && !RM && typed.id === l.id && typed.n < full.length;
        return (
          <div key={l.id} className={i === 0 ? 'new' : ''}>
            <span className="ts">[{time(l.ts)}]</span> <b>THALIA</b> ›{' '}
            {typing ? <span className="cur">{full.slice(0, typed.n)}</span> : <>{l.text}{l.hi && <> <em>{l.hi}</em></>}</>}
          </div>
        );
      })}
    </div>
  );
}

/** Scrolling activity waveform; bumps on each buy. */
export function Waveform({ bumpKey, color = '#7dff9b' }: { bumpKey: number; color?: string }) {
  const [pts, setPts] = useState(() => Array.from({ length: 61 }, (_, i) => 60 - 20 * Math.sin(i / 5) - Math.random() * 8));
  const bump = useRef(0);
  const tick = useRef(0);
  useEffect(() => { if (bumpKey > 0) bump.current = 1; }, [bumpKey]);
  useEffect(() => {
    const iv = setInterval(() => {
      tick.current++;
      setPts(p => [...p.slice(1), 60 - 18 * Math.sin(tick.current / 5) - Math.random() * 10 - bump.current * 40]);
      bump.current *= 0.3;
    }, prefersReducedMotion() ? 3000 : 450);
    return () => clearInterval(iv);
  }, []);
  return (
    <svg className="holo-wave" viewBox="0 0 600 120" preserveAspectRatio="none" aria-hidden="true">
      {Array.from({ length: 21 }, (_, i) => <line key={'v' + i} x1={i * 30} y1="0" x2={i * 30} y2="120" />)}
      {Array.from({ length: 7 }, (_, i) => <line key={'h' + i} x1="0" y1={i * 20} x2="600" y2={i * 20} />)}
      <polyline points={pts.map((y, i) => `${i * 10},${y.toFixed(1)}`).join(' ')} fill="none" stroke={color} strokeWidth="1.6" style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
    </svg>
  );
}
